import type { NotificationService } from '../../services/notification.service'; // NotificationService;
import type { NotificationData } from '../../services/notification.service';
import type { ModalStateService } from '../../services/modal-state.service';
import type { WayPurchaseModalStateService } from '../../components/way-purchase-modal/services/way-purchase-modal-state.service';
import type { MultiEligibleSelectionModalStateService } from '../../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import type { ActiveSubscriptionGateService } from '../../services/active-subscription-gate.service';
import {
  WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL,
  applyResolvedLprEntryTypeToNotification,
  isLprEntryCompleted,
  isWayMembershipLprRow,
  mergeLprEventPayloadIntoRow,
  resolveNotificationTypeFromLprEntry,
} from '../../notifications/notification.utils';
import { openUserReopenNotificationModal } from './notification-modal-open.util';
import { CAMERA_MONITOR_ONLY_PAYLOAD_FLAG } from '../constants/realtime.constants';
import { getUserTimezone } from './date.util';
import { coerceUsStateCode } from './split-license-plate-for-api';
import {
  isPrepaidRedeemLprNotification,
  normalizePrepaidRedeemNotificationForModal,
} from './get-lp-image-prepaid-redeem.util';

/** Toast copy when staff opens a cancelled order from the notifications list. */
export const CANCELLED_ORDER_NOTIFICATION_TOAST_DETAIL = 'This order has already been cancelled.';

function normalizeNotificationEventKey(value: any): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

function isCancelledOrderEventKey(key: string): boolean {
  return key === 'order-cancelled' || key === 'order-canceled';
}

/**
 * True when the list/history row (or NotificationData) represents a cancelled order.
 * These events have no modal layout — show a toast instead of an empty dialog.
 */
export function isCancelledOrderNotification(
  notification:
    | { event?: string; data?: NotificationData | Record<string, any> | null }
    | NotificationData
    | null
    | undefined
): boolean {
  if (!notification || typeof notification !== 'object') {
    return false;
  }

  const record = notification as Record<string, any>;

  if (isCancelledOrderEventKey(normalizeNotificationEventKey(record['event']))) {
    return true;
  }

  if (isCancelledOrderEventKey(normalizeNotificationEventKey(record['type']))) {
    return true;
  }

  const payload = record['data'];
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }

  const data = payload as Record<string, any>;
  if (isCancelledOrderEventKey(normalizeNotificationEventKey(data['type']))) {
    return true;
  }
  if (isCancelledOrderEventKey(normalizeNotificationEventKey(data['event']))) {
    return true;
  }

  const orderStatus = normalizeNotificationEventKey(
    data['order_status'] ?? data['orderStatus'] ?? data['status']
  );
  return orderStatus === 'cancelled' || orderStatus === 'canceled';
}

export function showCancelledOrderNotificationToast(messageService: NotificationService): void {
  messageService.add({
    severity: 'info',
    summary: 'Order cancelled',
    detail: CANCELLED_ORDER_NOTIFICATION_TOAST_DETAIL,
    life: 5000,
  });
}

/**
 * True when the live payload says the read came from a lane whose camera is marked
 * inactive. Such a read must not present itself — no modal, no toast — but the row is
 * otherwise a normal notification that staff can open and act on.
 *
 * Only live SSE payloads carry the flag; rows re-read from history are opened by an
 * explicit click, which is always allowed.
 */
export function isCameraMonitorOnlyNotification(
  notification:
    | { event?: string; data?: NotificationData | Record<string, unknown> | null }
    | NotificationData
    | null
    | undefined
): boolean {
  if (!notification || typeof notification !== 'object') {
    return false;
  }

  const record = notification as Record<string, unknown>;
  if (record[CAMERA_MONITOR_ONLY_PAYLOAD_FLAG] === true) {
    return true;
  }

  const payload = record['data'];
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return false;
  }

  const data = payload as Record<string, unknown>;
  if (data[CAMERA_MONITOR_ONLY_PAYLOAD_FLAG] === true) {
    return true;
  }

  const nested = data['data'];
  return (
    !!nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    (nested as Record<string, unknown>)[CAMERA_MONITOR_ONLY_PAYLOAD_FLAG] === true
  );
}

/** Staff camera / file upload via POST order/get-lp-image (not lane Pusher-only). */
export function isManualLprCaptureNotification(
  notification: NotificationData | null | undefined
): boolean {
  if (!notification?.data) {
    return false;
  }
  const d = notification.data as Record<string, any>;
  return d['is_lpr_upload'] === true || d['source_context'] === 'lpr';
}

/**
 * Extract license plate from notification data.
 * Accepts full NotificationData or the inner payload (e.g. notification.data).
 * Checks: data.data.plate, data.data.vehicle_license_plate, nested data.data.data, and data.plate/data.vehicle_license_plate.
 */
export function getLicensePlateFromNotification(
  data: NotificationData | Record<string, any> | null | undefined
): string {
  if (data === null || data === undefined) return 'Unknown';
  const d = data as Record<string, any>;
  const inner = d['data'];
  const plate =
    inner?.['plate'] ||
    inner?.['vehicle_license_plate'] ||
    inner?.['data']?.['plate'] ||
    inner?.['data']?.['vehicle_license_plate'] ||
    d['plate'] ||
    d['vehicle_license_plate'] ||
    d['license_plate'] ||
    '';
  return plate || 'Unknown';
}

/** Explicit state code carried by the notification payload (none when absent). */
function getStateFromNotification(
  data: NotificationData | Record<string, any> | null | undefined
): string {
  if (data === null || data === undefined) return '';
  const d = data as Record<string, any>;
  const inner = d['data'];
  return inner?.['state'] || inner?.['data']?.['state'] || d['state'] || '';
}

/**
 * Plate number for display only: strips the 2-char state prefix so the list shows just
 * the number (e.g. "CA8AUG441" → "8AUG441"). Only strips when the payload explicitly
 * carries a state code and the plate actually starts with it — a bare plate like
 * "OR12345" (no state field) is returned unchanged. Use {@link getLicensePlateFromNotification}
 * for matching/identity, which needs the full state+number value.
 */
export function getLicensePlateNumberFromNotification(
  data: NotificationData | Record<string, any> | null | undefined
): string {
  const full = getLicensePlateFromNotification(data);
  if (!full || full === 'Unknown') {
    return full;
  }
  const stateCode = coerceUsStateCode(getStateFromNotification(data));
  if (stateCode && full.length > stateCode.length && full.toUpperCase().startsWith(stateCode)) {
    return full.slice(stateCode.length);
  }
  return full;
}

/**
 * Resolve list/dropdown event key from row payload (handles completed orders with stale `event`).
 */
export function resolveNotificationEventKeyForDisplay(
  event: string | undefined,
  data?: NotificationData | Record<string, any> | null
): string {
  const row = extractLprRowPayloadFromListNotification(data ?? null);
  return resolveNotificationTypeFromLprEntry({ event: event ?? '', ...row });
}

function formatEventDisplayName(event: string): string {
  const formatted = event
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  if (formatted === 'Invalid License Plate') {
    return 'New Customer Request';
  }
  if (formatted === 'Create Payment Request') {
    return 'Payment Request';
  }
  if (formatted === 'Create Customer Request') {
    return 'New Customer Request';
  }
  if (formatted === 'Create Order Request') {
    return 'New Order Request';
  }
  if (formatted === 'Order Success') {
    return 'Order Success';
  }
  if (event === 'way-active-subscription') {
    return WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL;
  }
  if (event === 'multi-eligible-selection') {
    return 'Select Package';
  }

  return formatted;
}

import { isWayRedeemedNotificationRow } from './way-redeem-notification.util';

export { isWayRedeemedNotificationRow } from './way-redeem-notification.util';
export function resolveWayRedemptionChannelLabel(
  row: Record<string, any> | null | undefined
): string {
  const source = String(row?.['source'] ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (source === 'way_app') {
    return 'Way app';
  }
  if (source === 'way_web') {
    return 'Way web';
  }
  return 'Way';
}

function formatWayOrderSuccessDisplayName(row: Record<string, any>): string {
  return `Order success (${resolveWayRedemptionChannelLabel(row)})`;
}

function readWayPurchaseChannelSource(row: Record<string, any>): Record<string, any> {
  const purchase = row['purchase'];
  if (purchase && typeof purchase === 'object' && !Array.isArray(purchase)) {
    const purchaseRow = purchase as Record<string, any>;
    return {
      source: purchaseRow['purchase_channel'] ?? purchaseRow['source'] ?? row['source'],
    };
  }
  return row;
}

function formatWaySingleWashDisplayName(row: Record<string, any>): string {
  return `Single Wash (${resolveWayRedemptionChannelLabel(readWayPurchaseChannelSource(row))})`;
}

export function formatWayActiveMembershipDisplayName(_row: Record<string, any>): string {
  return WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL;
}

/**
 * Get formatted event name for display.
 * Pass optional notification `data` so completed rows are not labeled as new customer requests.
 */
export function getEventDisplayName(
  event: string,
  data?: NotificationData | Record<string, any> | null
): string {
  const row = extractLprRowPayloadFromListNotification(data ?? null);
  if (
    isPrepaidRedeemLprNotification(event, row) ||
    isPrepaidRedeemLprNotification(row['event'], row)
  ) {
    return 'Prepaid redemption';
  }
  const resolvedKey = resolveNotificationEventKeyForDisplay(event, data);
  // Live SSE Way membership often arrives as active-subscription with source_events/way_pass_id
  // and status:200 (not is_completed). After reload the DB row is completed active-subscription
  // — keep the same "Way Active Subscription" bell label when Way markers are present.
  if (
    resolvedKey === 'way-active-subscription' ||
    event === 'way-active-subscription' ||
    (isWayMembershipLprRow(row) && !isLprEntryCompleted(row)) ||
    (resolvedKey === 'active-subscription' && isWayRedeemedNotificationRow(row))
  ) {
    return formatWayActiveMembershipDisplayName(row);
  }
  if (resolvedKey === 'order-success' && isWayRedeemedNotificationRow(row)) {
    return formatWayOrderSuccessDisplayName(row);
  }
  if (resolvedKey === 'way-single-wash') {
    return formatWaySingleWashDisplayName(row);
  }
  return formatEventDisplayName(resolvedKey);
}

/**
 * Full LPR row payload from a notifications list/dropdown item (NotificationData wrapper).
 */
export function extractLprRowPayloadFromListNotification(
  src: NotificationData | Record<string, any> | null | undefined
): Record<string, any> {
  if (!src || typeof src !== 'object') {
    return {};
  }
  const record = src as Record<string, any>;
  const inner = record['data'];
  if (!inner || typeof inner !== 'object' || Array.isArray(inner)) {
    return {};
  }
  const row = inner as Record<string, any>;
  const nested = row['data'];
  const merged =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? { ...row, ...(nested as Record<string, any>) }
      : { ...row };
  return mergeLprEventPayloadIntoRow(merged);
}

/** Fallback when shop timezone is missing or invalid (never browser local). */
const NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE = 'America/Los_Angeles';

function formatNotificationTimestampInShopTimezone(date: Date, timeZone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    }).formatToParts(date);
    const month = parts.find(part => part.type === 'month')?.value ?? '';
    const day = parts.find(part => part.type === 'day')?.value ?? '';
    const year = parts.find(part => part.type === 'year')?.value ?? '';
    const hour = parts.find(part => part.type === 'hour')?.value ?? '';
    const minute = parts.find(part => part.type === 'minute')?.value ?? '';
    const dayPeriod = (parts.find(part => part.type === 'dayPeriod')?.value ?? '').toUpperCase();
    return `${month} ${day} ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch {
    if (timeZone !== NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE) {
      return formatNotificationTimestampInShopTimezone(
        date,
        NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE
      );
    }
    return '';
  }
}

/**
 * Format timestamp as "Month Date Year, Hour:Minute AM/PM" (e.g. "January 15 2024, 3:45 PM").
 * Uses selected shop timezone from localStorage when available; otherwise PST (America/Los_Angeles).
 */
export function getFormattedNotificationTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    const shopTimezone = getUserTimezone() || NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE;
    return formatNotificationTimestampInShopTimezone(date, shopTimezone);
  } catch {
    return timestamp;
  }
}

/**
 * Time-only label for a notification timestamp (e.g. "6:13 PM", "3:30 AM").
 * Formats at display time in the shop timezone (never mutates stored data) so the
 * dropdown rows can show just the time on the right while the date lives in the
 * per-day separator. Mirrors the timezone resolution used by
 * {@link getFormattedNotificationTimestamp}.
 */
export function getNotificationTimeOnly(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    const timeZone = getUserTimezone() || NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE;
    return formatNotificationTimeOnlyInShopTimezone(date, timeZone);
  } catch {
    return timestamp;
  }
}

function formatNotificationTimeOnlyInShopTimezone(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    }).format(date);
  } catch {
    if (timeZone !== NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE) {
      return formatNotificationTimeOnlyInShopTimezone(
        date,
        NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE
      );
    }
    return '';
  }
}

/** Calendar-day key (YYYY-MM-DD) for a date in the given timezone, for grouping. */
function getNotificationDayKeyInTimeZone(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone,
    }).format(date);
  } catch {
    if (timeZone !== NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE) {
      return getNotificationDayKeyInTimeZone(date, NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE);
    }
    return '';
  }
}

/** Separator label for a non-today group (e.g. "MAR 23, 2026") in the given timezone. */
function getNotificationDayLabelInTimeZone(date: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone,
    })
      .format(date)
      .toUpperCase();
  } catch {
    if (timeZone !== NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE) {
      return getNotificationDayLabelInTimeZone(date, NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE);
    }
    return '';
  }
}

/** A stored notification row shown in the dropdown/list. */
export interface NotificationRow {
  event: string;
  data: NotificationData;
  timestamp: string;
}

/** A calendar-day group of notifications with its separator label. */
export interface NotificationDayGroup {
  key: string;
  label: string;
  isToday: boolean;
  items: NotificationRow[];
}

/**
 * Group already-sorted (newest first) notifications by calendar day in the shop
 * timezone. Preserves incoming order, so groups come out newest-day-first and rows
 * within a group stay newest first. Today's group is labeled "TODAY"; other groups
 * use an uppercase "MON D, YYYY" label (e.g. "MAR 23, 2026"). Formatting is done at
 * display time only — stored timestamps are never mutated.
 */
export function groupNotificationsByDay(notifications: NotificationRow[]): NotificationDayGroup[] {
  const timeZone = getUserTimezone() || NOTIFICATION_TIMESTAMP_FALLBACK_TIMEZONE;
  const todayKey = getNotificationDayKeyInTimeZone(new Date(), timeZone);
  const groups: NotificationDayGroup[] = [];
  const groupsByKey = new Map<string, NotificationDayGroup>();

  for (const notification of notifications ?? []) {
    const date = new Date(notification.timestamp);
    const validDate = !isNaN(date.getTime());
    const key = validDate
      ? getNotificationDayKeyInTimeZone(date, timeZone)
      : `invalid:${notification.timestamp}`;

    let group = groupsByKey.get(key);
    if (!group) {
      const isToday = validDate && key === todayKey;
      group = {
        key,
        isToday,
        label: isToday
          ? 'TODAY'
          : validDate
            ? getNotificationDayLabelInTimeZone(date, timeZone)
            : '',
        items: [],
      };
      groupsByKey.set(key, group);
      groups.push(group);
    }
    group.items.push(notification);
  }

  return groups;
}

/**
 * Build NotificationData from a stored notification item for opening in modal.
 */
export function buildNotificationDataForModal(notification: {
  event: string;
  data: NotificationData | any;
  timestamp: string;
}): NotificationData {
  const row = extractLprRowPayloadFromListNotification(notification.data);
  const base: NotificationData = {
    type: notification.data.type,
    message: notification.data.message,
    timestamp:
      notification.data.timestamp instanceof Date
        ? notification.data.timestamp
        : new Date(notification.data.timestamp || notification.timestamp),
    data: row,
  };
  return normalizePrepaidRedeemNotificationForModal(
    applyResolvedLprEntryTypeToNotification(base, row)
  );
}

/**
 * Open a notification in the global modal (explicit user action from list/dropdown).
 * Cancelled orders show a toast only — no empty modal.
 * Uses forceShowModal so dismiss suppression and close-animation queuing do not block reopen.
 */
export function openNotificationInModal(
  notification: { event: string; data: NotificationData | any; timestamp: string },
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  messageService?: NotificationService,
  activeSubscriptionGate?: ActiveSubscriptionGateService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): void {
  try {
    if (messageService && isCancelledOrderNotification(notification)) {
      showCancelledOrderNotificationToast(messageService);
      return;
    }

    const notificationData = buildNotificationDataForModal(notification);
    if (messageService && isCancelledOrderNotification(notificationData)) {
      showCancelledOrderNotificationToast(messageService);
      return;
    }

    openUserReopenNotificationModal(
      modalStateService,
      wayPurchaseModalState,
      notificationData,
      activeSubscriptionGate,
      multiEligibleModalState
    );
  } catch (error) {
    console.error('Error opening notification modal:', error);
  }
}

/**
 * Opens NotificationData in the modal (detail view). Returns false when a toast was shown instead.
 */
export function openNotificationDataInModal(
  notificationData: NotificationData,
  modalStateService: ModalStateService,
  wayPurchaseModalState: WayPurchaseModalStateService,
  messageService: NotificationService,
  activeSubscriptionGate?: ActiveSubscriptionGateService,
  multiEligibleModalState?: MultiEligibleSelectionModalStateService
): boolean {
  if (isCancelledOrderNotification(notificationData)) {
    showCancelledOrderNotificationToast(messageService);
    return false;
  }
  openUserReopenNotificationModal(
    modalStateService,
    wayPurchaseModalState,
    notificationData,
    activeSubscriptionGate,
    multiEligibleModalState
  );
  return true;
}
